import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { motion } from "framer-motion";
import { Ticket, Tent } from "lucide-react";

const TRIP_META = {
  broadway: {
    icon: Ticket,
    accent: "bg-[#2F50A1]",
    tagline: "A Broadway show under the bright lights of New York City.",
  },
  overnight: {
    icon: Tent,
    accent: "bg-[#E9B44C]",
    tagline: "A camp overnight adventure with new friends and unforgettable memories.",
  },
};

export default function HomePage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/home")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  return (
    <AppLayout showAdminLink>
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <h1
          data-testid={TIDS.homeHeading}
          className="rrdc-heading text-3xl md:text-4xl font-bold text-[#111827] tracking-tight"
        >
          {data?.home?.heading || "Rolling River Day Camp Trip Sign Ups"}
        </h1>
        <p className="mt-2 text-[#4B5563] text-base no-hyphens">
          {data?.home?.subheading || "Choose a trip below to review the details and register your camper."}
        </p>
        {data?.home?.footer_note ? (
          <p className="mt-1 text-xs uppercase tracking-widest text-[#2F50A1] font-semibold">
            {data.home.footer_note}
          </p>
        ) : null}
      </motion.section>

      <section className="mt-8 space-y-4">
        {(data?.trips || []).map((t, idx) => {
          const meta = TRIP_META[t.key] || TRIP_META.broadway;
          const Icon = meta.icon;
          const testId = t.key === "broadway" ? TIDS.homeBroadwayCard : TIDS.homeOvernightCard;
          return (
            <motion.div
              key={t.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * idx + 0.1, duration: 0.3 }}
            >
              <Link
                to={`/trip/${t.key}`}
                data-testid={testId}
                className="block rrdc-card p-5 hover:shadow-md transition-shadow active:scale-[0.99]"
              >
                <div className="flex items-start gap-4">
                  <div className={`h-12 w-12 rounded-2xl ${meta.accent} text-white flex items-center justify-center shrink-0`}>
                    <Icon size={22} />
                  </div>
                  <div className="flex-1">
                    <div className="rrdc-heading text-xl font-bold text-[#111827]">{t.display_name}</div>
                    <div className="text-sm text-[#4B5563] mt-1 no-hyphens">{t.detail_line}</div>
                    <div className="text-sm text-[#4B5563] mt-1">{meta.tagline}</div>
                    <div className="mt-3 flex items-center gap-2">
                      {t.is_open ? (
                        <span className="text-xs px-2 py-1 rounded-full status-active font-semibold">Open for sign ups</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full status-awaiting_payment font-semibold">Sign ups closed</span>
                      )}
                      {t.price_cents > 0 ? (
                        <span className="text-xs text-[#4B5563]">${(t.price_cents / 100).toFixed(2)}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </section>

      {err ? <div className="mt-6 text-sm text-red-600">{err}</div> : null}

      <div className="mt-10 text-xs text-[#6B7280]">
        <Link to="/admin" data-testid={TIDS.homeAdminLink} className="underline">Office login</Link>
      </div>
    </AppLayout>
  );
}
